import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ChevronLeft, FileText, Loader2, Sparkles, TrendingUp,
  TrendingDown, Minus, Star, Target, AlertTriangle, Brain
} from 'lucide-react';

const TREND_ICON = { improving: TrendingUp, plateau: Minus, regression: TrendingDown, new: Star };
const TREND_COLOR = { improving: 'text-green-600 bg-green-50', plateau: 'text-amber-600 bg-amber-50', regression: 'text-red-600 bg-red-50', new: 'text-blue-600 bg-blue-50' };

export default function AIProgressReportPage() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [reports, setReports] = useState([]);
  const [activeReport, setActiveReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [childRes, reportsRes] = await Promise.all([
        api.get(`/children/${childId}`),
        api.get(`/children/${childId}/ai-progress-reports`),
      ]);
      setChild(childRes.data);
      setReports(reportsRes.data);
      if (reportsRes.data.length > 0) setActiveReport(reportsRes.data[0]);
    } catch { toast.error('Failed to load data'); }
    setLoading(false);
  }, [childId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const generateReport = async () => {
    setGenerating(true);
    try {
      const res = await api.get(`/children/${childId}/ai-progress-report`);
      setActiveReport(res.data);
      setReports(prev => [res.data, ...prev]);
      toast.success('Progress report generated!');
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || '';
      if (status === 429) toast.error(detail || 'AI budget exceeded.');
      else toast.error(detail || 'Failed to generate report');
    }
    setGenerating(false);
  };

  if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>;

  return (
    <div className="max-w-4xl mx-auto animate-fadeIn" data-testid="ai-progress-report-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(`/dashboard/ai-insights/${childId}`)} data-testid="back-btn">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Nunito' }}>
              AI Progress Reports
            </h1>
            <p className="text-sm text-slate-500">{child?.name}</p>
          </div>
        </div>
        <Button
          onClick={generateReport}
          disabled={generating}
          className="rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold gap-2"
          data-testid="generate-report-btn"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {generating ? 'Generating...' : 'Generate New Report'}
        </Button>
      </div>

      {/* AI DISCLAIMER */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6" data-testid="ai-disclaimer">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-amber-800">AI-Generated Report - For Reference Only</p>
            <p className="text-xs text-amber-700 mt-1 leading-relaxed">
              This report was generated by artificial intelligence and is intended as a supplementary tool only.
              Do not rely solely on this information for making decisions about a child's program.
              AI interpretations may be inaccurate or incomplete. Always use your professional judgment
              and direct observations when planning sessions and tracking progress.
            </p>
          </div>
        </div>
      </div>

      {/* Report selector */}
      {reports.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-3 mb-4">
          {reports.map(r => (
            <button
              key={r.report_id}
              onClick={() => setActiveReport(r)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${
                activeReport?.report_id === r.report_id ? 'bg-[#1E1B4B] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
              data-testid={`report-tab-${r.report_id}`}
            >
              {r.month || new Date(r.generated_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </button>
          ))}
        </div>
      )}

      {activeReport ? (
        <div className="space-y-4">
          {/* Overall Progress */}
          <Card className="rounded-3xl bg-gradient-to-br from-[#1E1B4B] to-[#312E81] text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <Brain className="w-5 h-5 text-[#22D3EE]" />
                <h3 className="font-bold text-lg" style={{ fontFamily: 'Nunito' }}>
                  {activeReport.month || 'Monthly'} Progress Summary
                </h3>
              </div>
              <p className="text-slate-200 text-sm leading-relaxed">{activeReport.overall_progress}</p>
              {activeReport.generated_at && (
                <p className="text-xs text-slate-400 mt-3">Generated {new Date(activeReport.generated_at).toLocaleString()}</p>
              )}
            </CardContent>
          </Card>

          {/* Skill Progression */}
          {activeReport.skill_progression?.length > 0 && (
            <Card className="rounded-3xl" data-testid="skill-progression">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#0E7490]" /> Skill Progression
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {activeReport.skill_progression.map((sp, i) => {
                    const TIcon = TREND_ICON[sp.trend] || Minus;
                    const colors = TREND_COLOR[sp.trend] || 'text-slate-600 bg-slate-50';
                    return (
                      <div key={`rpt-${i}`} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${colors}`}>
                          <TIcon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className="text-sm font-semibold text-slate-800">{sp.skill}</p>
                            <Badge className={`rounded-full text-[10px] capitalize ${colors}`}>{sp.trend}</Badge>
                          </div>
                          <p className="text-xs text-slate-500">{sp.detail}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Strengths */}
            {activeReport.strengths_observed?.length > 0 && (
              <Card className="rounded-3xl" data-testid="strengths">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Star className="w-4 h-4 text-green-500" /> Strengths Observed
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {activeReport.strengths_observed.map((s, i) => (
                      <li key={`rli-${i}`} className="flex items-start gap-2 text-sm text-slate-600">
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 shrink-0" />{s}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Areas of Focus */}
            {activeReport.areas_of_focus?.length > 0 && (
              <Card className="rounded-3xl" data-testid="areas-focus">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Target className="w-4 h-4 text-amber-500" /> Areas of Focus
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {activeReport.areas_of_focus.map((a, i) => (
                      <li key={`rli-${i}`} className="flex items-start gap-2 text-sm text-slate-600">
                        <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-1.5 shrink-0" />{a}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Patterns */}
          {activeReport.patterns?.length > 0 && (
            <Card className="rounded-3xl" data-testid="patterns">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Observed Patterns</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {activeReport.patterns.map((p, i) => (
                    <div key={`rpt-${i}`} className="p-3 bg-slate-50 rounded-xl">
                      <p className="text-sm font-semibold text-slate-700">{p.observation}</p>
                      <p className="text-xs text-slate-500 mt-1">{p.implication}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Goals */}
          {activeReport.goals_for_next_month?.length > 0 && (
            <Card className="rounded-3xl border-[#22D3EE]/20 bg-[#ECFEFF]/30" data-testid="goals">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Target className="w-4 h-4 text-[#0E7490]" /> Goals for Next Month
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {activeReport.goals_for_next_month.map((g, i) => (
                    <div key={`rfocus-${i}`} className="flex items-start gap-3 p-3 bg-white rounded-xl">
                      <Badge className="rounded-full bg-[#22D3EE] text-white text-xs shrink-0">{i + 1}</Badge>
                      <p className="text-sm text-slate-600">{g}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recommended Adjustments */}
          {activeReport.recommended_adjustments?.length > 0 && (
            <Card className="rounded-3xl" data-testid="adjustments">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Recommended Adjustments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {activeReport.recommended_adjustments.map((adj, i) => (
                    <div key={`rpt-${i}`} className="p-3 bg-slate-50 rounded-xl">
                      <Badge variant="outline" className="rounded-full text-[10px] capitalize mb-1">{adj.area}</Badge>
                      <p className="text-sm text-slate-600">{adj.suggestion}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Instructor Notes */}
          {activeReport.instructor_notes && (
            <Card className="rounded-3xl bg-gradient-to-br from-green-50 to-emerald-50 border-green-100" data-testid="instructor-notes">
              <CardContent className="p-5">
                <p className="text-sm font-semibold text-green-800 mb-1">Note for Instructor</p>
                <p className="text-sm text-green-700 leading-relaxed">{activeReport.instructor_notes}</p>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <Card className="rounded-3xl border-dashed border-2 border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FileText className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-lg font-bold text-slate-400 mb-2" style={{ fontFamily: 'Nunito' }}>No progress reports yet</p>
            <p className="text-sm text-slate-400 text-center max-w-md mb-4">
              Generate an AI progress report to see monthly analysis of this child's swim journey.
              Reports are also auto-generated on the 1st of each month.
            </p>
            <Button onClick={generateReport} disabled={generating} className="rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold gap-2" data-testid="generate-report-empty-btn">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Generate Progress Report
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
